/**
 * §P.9 · Ingresos por mes.
 *
 * ## La barra pinta lo cobrado sobre lo emitido
 *
 * De las cuatro cifras de dinero, esa es la única comparación que dice algo. Y es
 * **la que un club descubre tarde**: lleva tres meses contando como ingreso lo que
 * nunca llegó, porque el recibo emitido ya lo daba por bueno.
 *
 * ## Los meses vacíos se quedan
 *
 * El servidor los rellena en cero a propósito, y aquí no se filtran: sin ellos, la
 * serie uniría dos meses no consecutivos y el hueco —el agosto en que el club cierra—
 * desaparecería del año.
 *
 * ## Gráfica de `div`, no de librería
 *
 * «Lo que el club necesita es decidir, no admirar» (`BFF_BO.md` §7.6). Doce barras
 * con su cifra escrita al lado resuelven la decisión; 90 kB de librería para eso es
 * admirar. Y con la cifra escrita, la barra es un apoyo y no el dato.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, opcionesDeSesion } from '@/shared/api/client';
import { descargarFichero } from '@/shared/api/descargas';
import { aProblema, ErrorDeApi, type Problema } from '@/shared/api/errores';
import { Cargando, Error as PanelError, Vacio } from '@/shared/ui/Estados';
import { euros } from '@/shared/ui/formato';
import {
  anchoDeBarra,
  mesCorto,
  mesEnPalabras,
  porcentajeCobrado,
  type FilaDeIngresos,
  type Informe,
} from '@/club/informes/tipos';

export function Ingresos({ club }: { club: string }) {
  const [problema, setProblema] = useState<Problema | null>(null);
  const [descargando, setDescargando] = useState(false);

  const informe = useQuery({
    queryKey: ['informe-ingresos', club],
    queryFn: async () => {
      const { data, error } = await api.v1
        .clubs({ club })
        .reports.revenue.get({ query: {} });
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as Informe<FilaDeIngresos>;
    },
  });

  const descargar = async () => {
    setDescargando(true);
    setProblema(null);
    const fallo = await descargarFichero(
      `/v1/clubs/${club}/reports/revenue?format=csv`,
      'informe-de-ingresos.csv',
      opcionesDeSesion
    );
    setDescargando(false);
    if (fallo) setProblema(fallo);
  };

  if (informe.isPending) {
    return <Cargando filas={6} etiqueta="Calculando los ingresos" />;
  }

  if (informe.isError || !informe.data) {
    return (
      <PanelError
        problema={aProblema(informe.error)}
        reintentar={() => void informe.refetch()}
      />
    );
  }

  const filas = informe.data.rows;
  const totales = informe.data.totals;
  /*
   * El techo de las barras sale de lo emitido y **es común a los doce meses**: una
   * barra normalizada por mes pintaría todos al 100 % y no se vería ninguna
   * diferencia, que es lo único que se viene a ver aquí.
   */
  const techo = Math.max(...filas.map((f) => f.invoiced_cents), 0);
  const hayDinero = techo > 0;

  return (
    <section>
      <header className="titulo-fila">
        <h1>Ingresos</h1>
        <button
          type="button"
          onClick={() => void descargar()}
          disabled={descargando}
        >
          {descargando ? 'Preparando…' : 'Descargar en CSV'}
        </button>
      </header>

      <p className="tenue">
        Los últimos doce meses. <strong>Lo emitido no es lo cobrado</strong>, y
        la diferencia entre las dos es lo que conviene mirar todos los meses.
      </p>

      {problema && <PanelError problema={problema} />}

      {!hayDinero ? (
        <Vacio
          titulo="Todavía no se ha emitido nada"
          explicacion="Cuando se lance la primera remesa, aquí se verá mes a mes lo que se emitió, lo que llegó y lo que quedó pendiente."
        />
      ) : (
        <>
          <dl className="datos">
            <div>
              <dt>Emitido en el año</dt>
              <dd>{euros(totales['invoiced_cents'] ?? 0)}</dd>
            </div>
            <div>
              <dt>Cobrado</dt>
              <dd>{euros(totales['collected_cents'] ?? 0)}</dd>
            </div>
            <div>
              <dt>Pendiente</dt>
              <dd>{euros(totales['outstanding_cents'] ?? 0)}</dd>
            </div>
            <div>
              {/* Devuelto aparte de pendiente: son dos problemas distintos. */}
              <dt>Devuelto</dt>
              <dd>{euros(totales['failed_cents'] ?? 0)}</dd>
            </div>
          </dl>

          <ul className="barras">
            {filas.map((f) => {
              const cobrado = porcentajeCobrado(f);

              return (
                <li key={f.month}>
                  <span
                    className="barra-etiqueta"
                    title={mesEnPalabras(f.month)}
                  >
                    {mesCorto(f.month)}
                  </span>

                  <span className="barra-pista">
                    {/*
                      Dos barras superpuestas y no una: el fondo es lo emitido y el
                      relleno lo cobrado, así que el hueco que queda a la derecha **es**
                      lo que falta por entrar. Con dos barras al lado habría que
                      restarlas con la vista.
                    */}
                    <span
                      className="barra-emitido"
                      style={{
                        inlineSize: `${anchoDeBarra(f.invoiced_cents, techo)}%`,
                      }}
                    >
                      <span
                        className="barra-cobrado"
                        style={{
                          inlineSize: `${anchoDeBarra(
                            f.collected_cents,
                            f.invoiced_cents
                          )}%`,
                        }}
                      />
                    </span>
                  </span>

                  <span className="barra-cifra">
                    {euros(f.invoiced_cents)}
                    {cobrado === null ? (
                      ''
                    ) : (
                      <span className="tenue"> · {cobrado} % cobrado</span>
                    )}
                  </span>
                </li>
              );
            })}
          </ul>

          <h2 className="seccion">Mes a mes</h2>

          <div className="tabla-envoltura">
            <table className="tabla">
              <thead>
                <tr>
                  <th>Mes</th>
                  <th className="num">Emitido</th>
                  <th className="num">Cobrado</th>
                  <th className="num">Pendiente</th>
                  <th className="num">Devuelto</th>
                  <th className="num">Altas</th>
                  <th className="num">Bajas</th>
                  <th className="num">Alumnos</th>
                </tr>
              </thead>
              <tbody>
                {filas.map((f) => (
                  <tr key={f.month}>
                    <td>{mesEnPalabras(f.month)}</td>
                    <td className="num">{euros(f.invoiced_cents)}</td>
                    <td className="num">{euros(f.collected_cents)}</td>
                    <td className="num">{euros(f.outstanding_cents)}</td>
                    <td className="num">{euros(f.failed_cents)}</td>
                    <td className="num">
                      {f.joined === 0 ? '—' : `+${f.joined}`}
                    </td>
                    <td className="num">{f.left === 0 ? '—' : `−${f.left}`}</td>
                    <td className="num">{f.active_students}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/*
            Los totales **fuera de la tabla**, por lo mismo que el servidor los manda
            fuera de `rows`: una fila de totales dentro se ordena con las demás en
            cuanto alguien pulsa una cabecera, y entonces suma lo que no debe.
          */}
          <p className="tenue">
            Altas del año: {totales['joined'] ?? 0} · bajas:{' '}
            {totales['left'] ?? 0}.
          </p>
        </>
      )}
    </section>
  );
}
